import React, { useState, useEffect } from 'react';
import {
    IconX, IconLoader2, IconSearch, IconShieldCheck,
    IconChevronRight, IconChevronLeft,
    IconChevronsRight, IconChevronsLeft
} from '@tabler/icons-react';
import { useRoles, useRole, useAssignPermissionsToRole, usePatchRolePermissions } from '../../hooks/useRoles';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'react-hot-toast';

// Material UI Components
import { Paper, List, ListItem, ListItemIcon, ListItemText, Checkbox, Button, Typography, StyledEngineProvider } from '@mui/material';

const DISPLAY_LIMIT = 50;

export default function RolePermissionModal({ isOpen, onClose, organizations = [], initialOrgId = '', initialRoleId = '' }) {
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [leftDisplayCount, setLeftDisplayCount] = useState(DISPLAY_LIMIT);
    const [rightDisplayCount, setRightDisplayCount] = useState(DISPLAY_LIMIT);

    // Reset and seed state every time the modal opens or the target role changes
    useEffect(() => {
        if (isOpen) {
            setLeft([]);
            setRight([]);
            setChecked([]);
            setSearchTerm('');
            setLeftDisplayCount(DISPLAY_LIMIT);
            setRightDisplayCount(DISPLAY_LIMIT);
            setSelectedOrgId(initialOrgId || '');
            setSelectedRoleId(initialRoleId || '');
        }
    }, [isOpen, initialOrgId, initialRoleId]);

    const { data: rolesData, isLoading: rolesLoading } = useRoles(selectedOrgId);
    const roles = rolesData?.results || rolesData || [];

    const { data: roleDetail, isLoading: roleDetailLoading } = useRole(selectedRoleId, selectedOrgId);
    const { data: permsData, isLoading: permsLoading } = usePermissions();
    const allPermissions = permsData?.results || permsData || [];

    const assignPermissions = useAssignPermissionsToRole();
    const patchPermissions = usePatchRolePermissions();
    const isEditMode = !!initialRoleId;

    // Logic for Transfer List
    const intersection = (a, b) => a.filter((value) => b.indexOf(value) !== -1);
    const not = (a, b) => a.filter((value) => b.indexOf(value) === -1);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    useEffect(() => {
        if (isOpen && allPermissions.length > 0) {
            if (roleDetail?.permissions) {
                // Role API returns permissions as "app_label.codename" (e.g. "rbacapp.ts_act")
                // but allPermissions items may store codename as plain "ts_act".
                // Strip the app label prefix before comparing so both formats match.
                const stripPrefix = (code) =>
                    typeof code === 'string' && code.includes('.') ? code.split('.').pop() : code;

                const assigned = roleDetail.permissions
                    .map(p => {
                        if (typeof p === 'object') {
                            return allPermissions.find(ap =>
                                ap.id === p.id ||
                                stripPrefix(ap.codename) === stripPrefix(p.codename)
                            );
                        }
                        const pBase = stripPrefix(p);
                        return allPermissions.find(ap =>
                            ap.codename === p ||
                            stripPrefix(ap.codename) === pBase
                        );
                    })
                    .filter(Boolean);
                const available = not(allPermissions, assigned);
                setRight(assigned);
                setLeft(available);
                setChecked([]);
                setLeftDisplayCount(DISPLAY_LIMIT);
                setRightDisplayCount(DISPLAY_LIMIT);
            } else if (!selectedRoleId) {
                // No role selected yet (create mode) — show all as available
                setLeft(allPermissions);
                setRight([]);
                setChecked([]);
                setLeftDisplayCount(DISPLAY_LIMIT);
                setRightDisplayCount(DISPLAY_LIMIT);
            }
            // If a role IS selected but roleDetail hasn't loaded yet, do nothing —
            // keep the empty lists from the reset above until data arrives
        }
    }, [roleDetail, allPermissions, isOpen, selectedRoleId]);

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];
        if (currentIndex === -1) newChecked.push(value);
        else newChecked.splice(currentIndex, 1);
        setChecked(newChecked);
    };

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    // Move all items to right (assign all)
    const handleAllRight = () => {
        setRight(right.concat(left));
        setLeft([]);
        setChecked([]);
    };

    // Move all items to left (remove all)
    const handleAllLeft = () => {
        setLeft(left.concat(right));
        setRight([]);
        setChecked([]);
    };

    const renderList = (title, items, displayCount, onLoadMore) => {
        const filtered = items.filter(i =>
            (i.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (i.codename || "").toLowerCase().includes(searchTerm.toLowerCase())
        );

        // When searching, show all filtered results; otherwise limit display
        const isSearching = searchTerm.trim().length > 0;
        const displayedItems = isSearching ? filtered : filtered.slice(0, displayCount);
        const hasMore = !isSearching && filtered.length > displayCount;
        const remainingCount = filtered.length - displayCount;

        return (
            <Paper className="border border-border bg-bg-light" sx={{ width: '100%', height: 280, display: 'flex', flexDirection: 'column', boxShadow: 'none' }}>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 'bold', display: 'block', bgcolor: 'rgba(0,0,0,0.05)', color: 'var(--text-main)' }}>
                    {title} ({items.length}){!isSearching && items.length > DISPLAY_LIMIT && ` • Showing ${Math.min(displayCount, filtered.length)}`}
                </Typography>
                <List dense sx={{ flex: 1, overflow: 'auto' }} className="cus-scrollbar">
                    {displayedItems.map((value) => (
                        <ListItem key={value.id} button onClick={handleToggle(value)} sx={{ px: 1 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Checkbox checked={checked.indexOf(value) !== -1} size="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-main)' }}>{value.name}</Typography>}
                                secondary={<Typography sx={{ fontSize: '10px', color: 'gray' }}>{value.codename}</Typography>}
                            />
                        </ListItem>
                    ))}
                    {hasMore && (
                        <ListItem
                            button
                            onClick={() => onLoadMore(prev => prev + DISPLAY_LIMIT)}
                            sx={{ px: 1, justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.03)' }}
                        >
                            <Typography sx={{ fontSize: '11px', color: 'var(--primary)', fontWeight: 600 }}>
                                Load more ({remainingCount} remaining)
                            </Typography>
                        </ListItem>
                    )}
                </List>
            </Paper>
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const permissionCodenames = right.map(p => p.codename);
        try {
            if (isEditMode) {
                await patchPermissions.mutateAsync({
                    roleId: selectedRoleId,
                    permissions: permissionCodenames,
                    orgId: selectedOrgId,
                });
            } else {
                await assignPermissions.mutateAsync({
                    roleId: selectedRoleId,
                    permissions: permissionCodenames,
                    orgId: selectedOrgId,
                });
            }
            toast.success('Permissions updated successfully');
            onClose();
        } catch (error) {
            toast.error(error.message || 'Failed to update permissions');
        }
    };

    if (!isOpen) return null;

    return (
        <StyledEngineProvider injectFirst>
            <div className="fixed p-15 w-full max-w-[850px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[1001]">
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-xl border border-border'>
                    <h5 className="text-[16px] font-medium py-10 mb-20 text-center border-b border-border">Role Permissions</h5>
                    
                    <form onSubmit={handleSubmit}>
                        <div className='my-10 md:px-10 px-[7px] max-h-[75vh] overflow-auto cus-scrollbar'>
                            <div className='grid grid-cols-12 gap-x-20 gap-y-10'>
                                
                                {/* Selection Header */}
                                <div className='col-span-12 grid grid-cols-2 gap-15'>
                                    <div className='flex flex-col'>
                                        <label className='text-xs font-medium mb-1'>Organization *</label>
                                        <select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)} className='bg-bg-light border border-border p-2 rounded text-sm'>
                                            <option value=''>Select Organization</option>
                                            {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                                        </select>
                                    </div>
                                    <div className='flex flex-col'>
                                        <label className='text-xs font-medium mb-1'>Role *</label>
                                        <select value={selectedRoleId} onChange={(e) => setSelectedRoleId(e.target.value)} disabled={!selectedOrgId} className='bg-bg-light border border-border p-2 rounded text-sm'>
                                            <option value=''>Select Role</option>
                                            {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Search and Transfer List */}
                                <div className='col-span-12 mt-10 border-t border-border pt-15'>
                                    <div className='flex justify-between items-center mb-10'>
                                        <div className="relative">
                                            <IconSearch size={14} className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Filter permissions..."
                                                className="bg-bg-light border border-border pl-30 pr-10 py-1 rounded text-xs w-48 focus:outline-none"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <div className="flex-1">{renderList('Available', left, leftDisplayCount, setLeftDisplayCount)}</div>
                                        <div className="flex flex-col gap-2">
                                            <Button variant="outlined" size="small" onClick={handleAllRight} disabled={left.length === 0} sx={{ minWidth: 40, borderColor: 'var(--border)' }} title="Move all to assigned">
                                                <IconChevronsRight size={18} />
                                            </Button>
                                            <Button variant="outlined" size="small" onClick={handleCheckedRight} disabled={leftChecked.length === 0} sx={{ minWidth: 40, borderColor: 'var(--border)' }} title="Move selected to assigned">
                                                <IconChevronRight size={18} />
                                            </Button>
                                            <Button variant="outlined" size="small" onClick={handleCheckedLeft} disabled={rightChecked.length === 0} sx={{ minWidth: 40, borderColor: 'var(--border)' }} title="Move selected to available">
                                                <IconChevronLeft size={18} />
                                            </Button>
                                            <Button variant="outlined" size="small" onClick={handleAllLeft} disabled={right.length === 0} sx={{ minWidth: 40, borderColor: 'var(--border)' }} title="Move all to available">
                                                <IconChevronsLeft size={18} />
                                            </Button>
                                        </div>
                                        <div className="flex-1">{renderList('Assigned', right, rightDisplayCount, setRightDisplayCount)}</div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className='flex gap-10 justify-end p-10 border-t border-border mt-10'>
                            <button type='button' onClick={onClose} className='btn btn-outline-secondary btn-sm'>Cancel</button>
                            <button type='submit' className='btn btn-primary btn-sm' disabled={!selectedRoleId || assignPermissions.isPending || patchPermissions.isPending}>
                                {assignPermissions.isPending ? 'Saving...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            {/* Backdrop */}
            <div onClick={onClose} className='fixed z-[1000] w-full h-full left-0 top-0 bg-black/50 backdrop-blur-[2px]'></div>
        </StyledEngineProvider>
    );
}